import io from 'socket.io-client';
import { getBackendUrl } from '../config';
import { readToken, refreshAccessToken } from './authSession';
import { startTracking, stopTracking, getIdleMs, onBecameActive } from './presenceTracker';

const HEARTBEAT_INTERVAL_MS = 30_000;

class SocketWorker {
  constructor(companyId, userId) {
    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.socket = null;
      this.heartbeatInterval = null;
      this.unsubscribeBecameActive = null;
      this.refreshingToken = false;
      this.configureSocket();
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    const backendBaseUrl = (
      getBackendUrl() ||
      process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    ).replace(/\/$/, '');

    this.socket = io(`${backendBaseUrl}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      // Função → o socket.io relê o token a cada (re)conexão, pegando um token
      // renovado sem precisar recriar a conexão.
      auth: (cb) => cb({ token: readToken() }),
    });

    this.socket.on('connect', () => {
      this.refreshingToken = false;
      this.startHeartbeat();
    });

    this.socket.on('connect_error', (err) => {
      // Token expirado no handshake: tenta 1 refresh e deixa o socket.io
      // reconectar (a função auth relê o token novo). Sem loop: só uma tentativa
      // até um connect bem-sucedido resetar a flag.
      if (err?.message === 'unauthorized' && !this.refreshingToken) {
        this.refreshingToken = true;
        this.tryRefreshToken();
      }
    });

    this.socket.on('disconnect', () => {
      // disconnected
      this.stopHeartbeat();
      this.reconnectAfterDelay();
    });
  }

  async tryRefreshToken() {
    // Single-flight compartilhado com o interceptor do axios: se o HTTP já está
    // renovando, aproveitamos o mesmo POST em vez de disparar um concorrente.
    const token = await refreshAccessToken();
    if (!token) {
      // Sessão inválida; interrompe reconexões para não iterar em vão.
      this.socket?.close();
    }
    // Sucesso: o socket.io continua tentando reconectar e a função `auth` relê o
    // token novo do localStorage na próxima tentativa.
  }

  /**
   * Heartbeat de presença: informa ao backend o tempo de inatividade a cada
   * 30s (decide online/away no servidor). Emite um batimento imediato no
   * connect — evita flapping em login/F5 — e outro na volta da ausência.
   */
  startHeartbeat() {
    this.stopHeartbeat();
    startTracking();
    const send = (idleMs) => {
      if (this.socket?.connected) {
        this.socket.emit('heartbeat', { idleMs: idleMs ?? getIdleMs() });
      }
    };
    send(0);
    this.heartbeatInterval = setInterval(() => send(), HEARTBEAT_INTERVAL_MS);
    this.unsubscribeBecameActive = onBecameActive(() => send(0));
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.unsubscribeBecameActive) {
      this.unsubscribeBecameActive();
      this.unsubscribeBecameActive = null;
    }
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      // console.log("Desconectando do servidor Socket.IO:", event, callback);
      if (callback) {
        // Desconecta um ouvinte específico
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter((cb) => cb !== callback);
      } else {
        // console.log("DELETOU EVENTOS DO SOCKET:", this.eventListeners[event]);

        // Desconecta todos os ouvintes do evento
        this.eventListeners[event].forEach((cb) => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
      // console.log("EVENTOS DO SOCKET:", this.eventListeners);
    }
  }

  disconnect() {
    this.stopHeartbeat();
    stopTracking();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      SocketWorker.instance = null;
      // disconnected manually
    }
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        // reconnecting
        this.connect();
      }
    }, 1000);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    }
  }
}

// const instance = (companyId, userId) => new SocketWorker(companyId,userId);
const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
